// /composables/useSeo.ts
import contactNumber from "../data/contactNumber.js";

// Helper function to format phone number for meta tags
const formatPhoneForMeta = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `+1-${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

// Helper function to format phone number for tel: link
const formatPhoneForTel = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, "");
  return `+${cleaned.startsWith("1") ? cleaned : "1" + cleaned}`;
};

const phoneNumberMeta = formatPhoneForMeta(contactNumber);
const phoneNumberTel = formatPhoneForTel(contactNumber);

export function useSeoBase(opts?: {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
  hreflang?: string;
}) {
  const siteUrl = "https://junkguysmn.com"; // set your real domain
  const canonical = opts?.canonical || siteUrl;
  const title =
    opts?.title ||
    "Junk Guys MN - Junk Removal & Light Demolition in Minnesota";
  const description =
    opts?.description ||
    "Same-day junk removal and light demo across the Twin Cities. Transparent pricing, fast booking, responsible disposal.";
  const ogImage = opts?.ogImage || `${siteUrl}/og.jpg`;
  const hreflang = opts?.hreflang || "en-US";

  useHead({
    title,
    meta: [
      // Basic meta tags
      { charset: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "description", content: description },
      {
        name: "keywords",
        content:
          "junk removal, demolition, Minnesota, Twin Cities, same-day service, appliance removal, furniture removal",
      },
      { name: "author", content: "Junk Guys MN" },
      {
        name: "robots",
        content: opts?.noindex ? "noindex,follow" : "index,follow",
      },

      // Open Graph
      { property: "og:site_name", content: "Junk Guys MN" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical },
      { property: "og:image", content: ogImage },
      { property: "og:locale", content: "en_US" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },

      // Twitter
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
      { name: "twitter:site", content: "@junkguysmn" },

      // Additional SEO
      { name: "geo.region", content: "US-MN" },
      { name: "geo.placename", content: "Minnesota" },
      { name: "geo.position", content: "44.9778;-93.2650" },
      { name: "ICBM", content: "44.9778, -93.2650" },

      // Business info
      { name: "business:contact_data:street_address", content: "Minnesota" },
      { name: "business:contact_data:locality", content: "Twin Cities" },
      { name: "business:contact_data:region", content: "MN" },
      { name: "business:contact_data:postal_code", content: "55101" },
      { name: "business:contact_data:country_name", content: "United States" },
      {
        name: "business:contact_data:phone_number",
        content: phoneNumberMeta,
      },
    ],
    link: [
      { rel: "canonical", href: canonical },
      { rel: "alternate", hreflang, href: canonical }, // hreflang self
      { rel: "alternate", hreflang: "x-default", href: canonical },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", href: "/jg-logo-t.webp" },
      { rel: "manifest", href: "/manifest.json" },
    ],
  });
}

export function useJsonLd(graph: any[]) {
  useHead({
    script: [{ type: "application/ld+json", innerHTML: JSON.stringify(graph) }],
  });
}

export const ORG_ID = "https://junkguysmn.com/#org";
export const LB_ID = "https://junkguysmn.com/#local";

export function orgNode() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: "Junk Guys MN",
    url: "https://junkguysmn.com",
    logo: "https://junkguysmn.com/jg-logo-t.webp",
    sameAs: [
      "https://www.facebook.com/junkguysmn",
      "https://www.instagram.com/junkguysmn",
      "https://share.google/HGj3JP1yjSnXfG6Hm",
    ],
  };
}

export function localBusinessNode() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": LB_ID,
    name: "Junk Guys MN",
    image: "https://junkguysmn.com/jg-logo-t.webp",
    telephone: contactNumber,
    address: {
      "@type": "PostalAddress",
      streetAddress: "460 9th Ave NW",
      addressLocality: "New Brighton",
      addressRegion: "MN",
      postalCode: "55112",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 45.0594,
      longitude: -93.1977,
    },
    areaServed: {
      "@type": "Place",
      name: "Minneapolis-St. Paul Twin Cities Metro Area",
    },
    priceRange: "$$",
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ],
        opens: "08:00",
        closes: "21:00",
      },
    ],
    parentOrganization: { "@id": ORG_ID },
  };
}

export function serviceNode(service: string, city?: string, url?: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: city ? `${service} in ${city}, MN` : service,
    areaServed: city ? { "@type": "City", name: city } : "Minnesota",
    provider: { "@id": LB_ID },
    url,
  };
}

export function breadcrumbNode(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function websiteNode() {
  return {
    "@type": "WebSite",
    name: "Junk Guys MN",
    alternateName: "Junk Guys Minnesota",
    url: "https://junkguysmn.com/",
  };
}

export function faqNode(faqs: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((qa) => ({
      "@type": "Question",
      name: qa.q,
      acceptedAnswer: { "@type": "Answer", text: qa.a },
    })),
  };
}
